import { ChevronLeft, ChevronRight } from 'lucide-react';

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100, 200];

type Props = {
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  disabled?: boolean;
  pageSizeOptions?: number[];
};

/**
 * Footer shown under a list even when everything fits on one page, so the row
 * count and the rows-per-page selector stay reachable.
 */
export function ListPaginationFooter({
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  onLimitChange,
  disabled = false,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
}: Props) {
  const lastPage = Math.max(1, totalPages);
  const currentPage = Math.min(Math.max(1, page), lastPage);
  const firstRow = total === 0 ? 0 : (currentPage - 1) * limit + 1;
  const lastRow = Math.min(currentPage * limit, total);

  const pageNumbers = Array.from({ length: Math.min(5, lastPage) }, (_, i) => {
    const start = Math.max(1, Math.min(lastPage - 4, currentPage - 2));
    return start + i;
  });

  const options = pageSizeOptions.includes(limit)
    ? pageSizeOptions
    : [...pageSizeOptions, limit].sort((a, b) => a - b);

  return (
    <div className="dt-list-toolbar bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <p className="text-sm text-gray-700">
            {total === 0 ? (
              'Aucun résultat'
            ) : (
              <>
                Affichage de <span className="font-medium">{firstRow}</span> à{' '}
                <span className="font-medium">{lastRow}</span> sur{' '}
                <span className="font-medium">{total}</span> résultat{total > 1 ? 's' : ''}
              </>
            )}
          </p>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <span className="whitespace-nowrap">Lignes par page</span>
            <select
              value={limit}
              disabled={disabled}
              onChange={(e) => onLimitChange(Number(e.target.value))}
              className="input-field h-9 w-20 py-1"
            >
              {options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <p className="text-sm text-gray-500 sm:hidden">
            Page {currentPage} / {lastPage}
          </p>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, currentPage - 1))}
              disabled={disabled || currentPage === 1}
              aria-label="Page précédente"
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>

            {pageNumbers.map((pageNum) => (
              <button
                key={pageNum}
                type="button"
                onClick={() => onPageChange(pageNum)}
                disabled={disabled}
                aria-current={currentPage === pageNum ? 'page' : undefined}
                className={`relative hidden sm:inline-flex items-center px-4 py-2 border text-sm font-medium disabled:cursor-not-allowed ${
                  currentPage === pageNum
                    ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {pageNum}
              </button>
            ))}

            <button
              type="button"
              onClick={() => onPageChange(Math.min(lastPage, currentPage + 1))}
              disabled={disabled || currentPage === lastPage}
              aria-label="Page suivante"
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}

export default ListPaginationFooter;
